/**
 * Single mapper module for Issues/Work items: API shaping, static options, Kanban, list visuals.
 */

import { LuZap } from "react-icons/lu";
import {
  RiTaskLine,
  RiBugLine,
  RiArrowUpDoubleLine,
  RiArrowUpSLine,
  RiSubtractLine,
  RiArrowDownDoubleLine,
  RiArrowDownFill,
  RiSubtractFill,
  RiArrowUpFill,
  RiErrorWarningFill,
  RiArrowUpCircleFill,
  RiArrowDownCircleFill,
  RiAlarmWarningFill,
  RiCircleFill,
  RiPlayCircleFill,
  RiStopCircleFill,
  RiCheckboxCircleFill,
  RiEyeLine,
  RiPauseCircleLine,
  RiTestTubeLine,
  RiFileList3Line,
} from "react-icons/ri";
import { getListFromApiResponse as getList } from "../utils/apiListMappers";
import { APP_ROLE } from "../../permissions/roleKinds";
import { normalizeRoleName, resolveRoleKind } from "../../permissions/resolvePermissions";
import {
  DEFAULT_WORK_ITEM_STATUS_CATEGORY_FILTER_OPTIONS,
  DEFAULT_WORK_ITEM_STATUS_CATEGORY_ORDER,
  WORK_ITEM_STATUS_CATEGORY_THEME,
  getWorkItemStatusCategoryBadgeStyle,
  getWorkItemStatusCategoryKanbanClass,
  getWorkItemStatusCategoryLabel,
  getWorkItemStatusCategorySortIndex,
  getKanbanStatusCategorySortIndex,
  KANBAN_BOARD_STATUS_CATEGORY_ORDER,
  normalizeWorkItemStatusCategoryKey,
  normalizeWorkItemStatusCategoriesForApi,
  PROJECT_KANBAN_STATUS_CATEGORY_ORDER,
  PROJECT_STATUS_DROPDOWN_CATEGORY_ORDER,
  workItemStatusCategorySortKey,
} from "@/constants/workItemStatusCategoryTheme";

export {
  DEFAULT_WORK_ITEM_STATUS_CATEGORY_FILTER_OPTIONS,
  DEFAULT_WORK_ITEM_STATUS_CATEGORY_ORDER,
  PROJECT_KANBAN_STATUS_CATEGORY_ORDER,
  PROJECT_STATUS_DROPDOWN_CATEGORY_ORDER,
  normalizeWorkItemStatusCategoriesForApi,
} from "@/constants/workItemStatusCategoryTheme";
export {
  buildProjectStatusOptionsByCategory,
  resolveProjectStatusOptionValue,
} from "@/constants/workItemStatusCategoryTheme";

function filterValuesToArray(val) {
  if (val == null || val === "") return [];
  return Array.isArray(val)
    ? val.filter((v) => v != null && String(v).trim() !== "")
    : [String(val)];
}

/** Split status filter values into API `statusCategories` vs workflow `statusIds`. */
export function partitionWorkItemListStatusFilter(val) {
  const arr = filterValuesToArray(val);
  const categories = arr.filter(isWorkItemStatusCategoryApiValue);
  const ids = arr.filter((x) => !isWorkItemStatusCategoryApiValue(x));
  return { categories, ids };
}

export const getListFromApiResponse = getList;

/** Prefer list from prop if non-empty, else from store. Both can be API-shaped. */
export function preferListFromPropOrStore(propList, storeData) {
  const fromProp = getListFromApiResponse(propList);
  if (fromProp.length) return fromProp;
  return getListFromApiResponse(storeData);
}

/** Return projects that are allowed (client not on hold). isClientOnHold(client) => boolean. */
export function getAllowedProjects(projectsList, clientsList, isClientOnHold) {
  const projects = getListFromApiResponse(projectsList);
  const clients = getListFromApiResponse(clientsList);
  if (!projects.length) return [];
  if (!clients.length) return projects;
  return projects.filter((p) => {
    const client = clients.find(
      (c) => (c.clientName ?? c.name) === (p.clientName ?? p.client?.name)
    );
    return !client ? true : !isClientOnHold(client);
  });
}

/** Map project to parent option { value, label }. Value/label always strings for dropdown. */
export function mapProjectToParentOption(p) {
  const value = p?.id ?? p?.projectId;
  const label = p?.projectName ?? p?.name ?? "";
  return {
    value: value != null ? String(value) : "",
    label: label != null && String(label).trim() ? String(label).trim() : "Unnamed Project",
  };
}

/** Team multiselect options for work item filters ({ label, value } with string ids). */
export function mapTeamsToFilterOptions(teamsData) {
  return getListFromApiResponse(teamsData)
    .map((team) => {
      const id = team?.id ?? team?.teamId;
      const label = String(team?.name ?? team?.teamName ?? "").trim();
      if (id == null || id === "" || !label) return null;
      return { label, value: String(id) };
    })
    .filter(Boolean);
}

/** Work item list: project name only. */
export function formatWorkItemProjectName(issue) {
  if (!issue || typeof issue !== "object") return "";
  return String(
    issue.projectName ?? issue.project?.name ?? issue.project?.projectName ?? ""
  ).trim();
}

export function getWorkItemClientProjectParts(issue) {
  if (!issue || typeof issue !== "object") return { clientName: "", projectName: "" };
  const projectName = formatWorkItemProjectName(issue);
  const clientName = String(
    issue.clientName ??
      issue.client?.name ??
      issue.client?.clientName ??
      issue.project?.client?.name ??
      ""
  ).trim();
  return { clientName, projectName };
}

/** Kanban cards / create-task project dropdown: `Client name - Project name` when both exist. */
export function formatWorkItemProjectClientLabel(issue) {
  const { clientName, projectName } = getWorkItemClientProjectParts(issue);
  if (!projectName && !clientName) return "";
  if (!projectName) return clientName;
  if (!clientName) return projectName;
  return `${clientName} - ${projectName}`;
}

/** Create-task modal project dropdown — same combined label as Kanban cards. */
export function mapProjectToCreateTaskOption(p) {
  const option = mapProjectToParentOption(p);
  const clientName = p?.client?.name ?? p?.clientName ?? p?.client?.clientName ?? "";
  const trimmedClient = clientName != null ? String(clientName).trim() : "";
  if (!trimmedClient) return option;
  return { ...option, label: `${trimmedClient} - ${option.label}` };
}

/**
 * Two-letter (or one-letter) initials for Avatar `name` when length is 1–2.
 * @param {object} user
 */
export function getUserAssigneeInitials(user) {
  if (user == null || typeof user !== "object") return "?";
  const firstName = String(user?.firstName ?? "").trim();
  const lastName = String(user?.lastName ?? "").trim();
  if (firstName && lastName) {
    return [firstName.charAt(0), lastName.charAt(0)].join("").toUpperCase();
  }
  const combined = [firstName, lastName].filter(Boolean).join(" ").trim();
  if (combined.length >= 2) {
    return combined.slice(0, 2).toUpperCase();
  }
  if (combined.length === 1) {
    return combined.charAt(0).toUpperCase();
  }
  const fromName = String(user?.name ?? user?.userName ?? user?.email ?? "").trim();
  if (fromName.length >= 2) return fromName.slice(0, 2).toUpperCase();
  if (fromName.length === 1) return fromName.charAt(0).toUpperCase();
  return "?";
}

/** API role codes / names allowed as Epic assignee (Admin + Project Manager only). */
const EPIC_ASSIGNEE_ROLE_ENUMS = new Set([
  "SUPER_ADMIN",
  "ADMINISTRATOR",
  "ADMIN",
  "OWNER",
  "PROJECT_MANAGER",
]);

/**
 * Epic work items may only be assigned to Admin or Project Manager users.
 * @param {object} user — row from GET /users/all
 */
export function isUserEligibleEpicAssignee(user) {
  const role = user?.role;
  const roleName =
    typeof role === "object" && role != null
      ? String(role.name ?? "").trim()
      : String(role ?? user?.roleName ?? "").trim();
  const roleCode =
    typeof role === "object" && role != null ? String(role.code ?? role.slug ?? "").trim() : "";
  const candidates = [roleName, roleCode].filter(Boolean);
  if (candidates.length === 0) return false;

  for (const raw of candidates) {
    const enumKey = raw.toUpperCase().replace(/\s+/g, "_");
    if (EPIC_ASSIGNEE_ROLE_ENUMS.has(enumKey)) return true;
  }

  const kind = resolveRoleKind(
    typeof role === "object" && role != null ? role : { name: roleName || roleCode }
  );
  if (kind === APP_ROLE.ADMIN) return true;

  const n = normalizeRoleName(
    typeof role === "object" && role != null ? role : { name: roleName || roleCode }
  ).replace(/_/g, " ");
  if (/\b(super\s*admin|administrator|admin|owner)\b/.test(n)) return true;
  if (/\bproject\s*manager\b/.test(n)) return true;

  return false;
}

/** @param {object[]} users */
export function filterUsersForEpicAssignee(users) {
  if (!Array.isArray(users)) return [];
  return users.filter(isUserEligibleEpicAssignee);
}

/** @param {string} label — work item type option label from create form */
export function isEpicWorkItemTypeLabel(label) {
  return (
    String(label ?? "")
      .trim()
      .toLowerCase() === "epic"
  );
}

/** System-list type option ids whose label is Epic. */
export function getEpicTypeIdsFromOptions(typeOptions) {
  const list = Array.isArray(typeOptions) ? typeOptions : [];
  const ids = [];
  for (const opt of list) {
    const label = typeof opt === "string" ? opt : (opt?.label ?? "");
    if (!isEpicWorkItemTypeLabel(label)) continue;
    const id = typeof opt === "string" ? null : (opt?.value ?? opt?.id);
    if (id != null && String(id).trim() !== "") ids.push(String(id).trim());
  }
  return ids;
}

/** @param {object} issue — normalized work item row */
export function isEpicWorkItemRow(issue, typeOptions) {
  if (isEpicWorkItemTypeLabel(issue?.type)) return true;
  const tid = issue?.typeId != null ? String(issue.typeId) : "";
  if (!tid) return false;
  return getEpicTypeIdsFromOptions(typeOptions).includes(tid);
}

/**
 * Epic → Admin + Project Manager only; Task, Bug, and other types → all users.
 * @param {object[]} users
 * @param {string} typeLabel
 */
export function filterAssigneesForWorkItemType(users, typeLabel) {
  const list = Array.isArray(users) ? users : [];
  return isEpicWorkItemTypeLabel(typeLabel) ? filterUsersForEpicAssignee(list) : list;
}

/** Map a user API object to dropdown option { value, label, avatar, showAvatar } (avatar in list only). */
export function mapUserToAssigneeOptionForDropdown(user, _index) {
  const firstName = user?.firstName ?? "";
  const lastName = user?.lastName ?? "";
  const fullName =
    [firstName, lastName].filter(Boolean).join(" ").trim() ||
    String(user?.name ?? user?.userName ?? "").trim() ||
    String(user?.email ?? "").trim() ||
    "N/A";
  const initials = getUserAssigneeInitials(user);
  const id = user?.id ?? user?.userId;
  return {
    value: id != null ? String(id) : "",
    label: fullName,
    avatar: initials,
    showAvatar: true,
  };
}

/**
 * Assignee row in global filters — value is user id when available (matches API + client filter).
 */
export function mapUserToAssigneeOptionForFilter(user) {
  const firstName = user?.firstName ?? "";
  const lastName = user?.lastName ?? "";
  const label =
    [firstName, lastName].filter(Boolean).join(" ").trim() ||
    String(user?.name ?? user?.fullName ?? user?.userName ?? "").trim() ||
    String(user?.email ?? "").trim() ||
    "N/A";
  const id = user?.id ?? user?.userId;
  const initials = getUserAssigneeInitials(user);
  return {
    label,
    value: id != null ? String(id) : label,
    avatar: initials,
    showAvatar: true,
  };
}

/** Map a user API object to Kanban assignee option { id, name, initials, color }. */
export function mapUserToAssigneeOptionForKanban(user, index, getColorByIndex) {
  const firstName = user?.firstName ?? "";
  const lastName = user?.lastName ?? "";
  const fromParts = [firstName, lastName].filter(Boolean).join(" ").trim();
  const name =
    fromParts ||
    String(user?.name ?? user?.fullName ?? user?.userName ?? "").trim() ||
    String(user?.email ?? "").trim() ||
    "N/A";
  const hasName = fromParts.length > 0;
  const initials = hasName
    ? [firstName.charAt(0), lastName.charAt(0)].filter(Boolean).join("").toUpperCase()
    : getUserAssigneeInitials(user);
  const userId = user?.id ?? user?.userId ?? null;
  return {
    id: userId != null ? String(userId) : `anon-${index}-${String(name).slice(0, 12)}`,
    userId,
    name,
    initials: initials || "?",
    color: getColorByIndex ? getColorByIndex(index) : "",
  };
}

/**
 * Stable identifier for PATCH/query bodies (UUID). Prefer API id fields before label-like fields.
 * Some list rows use `value` for the UUID when `id` is absent; never use `name` before `value`.
 */
export function getSystemListItemId(item) {
  if (item == null || typeof item === "string") return null;
  if (item.tag && typeof item.tag === "object") {
    const tid =
      item.tag.id ??
      item.tag.priorityId ??
      item.tag.listItemId ??
      item.tag.systemListItemId ??
      item.tag.value ??
      null;
    if (tid != null) return tid;
  }
  return (
    item.id ??
    item.priorityId ??
    item.listItemId ??
    item.systemListItemId ??
    item.systemListId ??
    item.value ??
    null
  );
}

/** Map system list item (string or { id, name, value }) to dropdown option { id, value, label, ...extra }. */
export function mapSystemListItemToOption(item, iconMap = {}) {
  if (typeof item === "string") {
    const extra = iconMap[item] || {};
    return { id: item, value: item, label: item, ...extra };
  }
  /** Some list types (e.g. tags) nest the row under `tag` */
  const nested = item?.tag && typeof item.tag === "object" ? item.tag : null;
  const stableId = nested
    ? (getSystemListItemId(nested) ?? getSystemListItemId(item))
    : getSystemListItemId(item);
  let label =
    nested?.name ??
    nested?.label ??
    nested?.displayName ??
    item.name ??
    item.label ??
    item.displayName ??
    item.title ??
    item.code ??
    "";
  if (!label && stableId != null) label = String(stableId);
  /** Never use display `name` as option value — backend expects list item id (UUID). */
  const value =
    stableId != null && String(stableId).trim() !== ""
      ? String(stableId).trim()
      : item.value != null && String(item.value).trim() !== ""
        ? String(item.value).trim()
        : "";
  const iconKeyMatch = (key) => {
    if (key == null || key === "" || typeof iconMap !== "object") return {};
    const ks = String(key).trim();
    if (iconMap[ks]) return iconMap[ks];
    const found = Object.keys(iconMap).find((k) => k.toLowerCase() === ks.toLowerCase());
    return found ? iconMap[found] : {};
  };
  const extra =
    iconMap[label] ||
    iconMap[value] ||
    iconMap[item.name] ||
    iconKeyMatch(label) ||
    iconKeyMatch(value) ||
    {};
  const id = nested?.id ?? item.id ?? stableId ?? value;
  return { id, value, label, ...extra };
}

/** Map system list API array to simple string array for filter dropdowns (type, priority). */
export function mapSystemListToFilterOptions(rawList) {
  const list = getListFromApiResponse(rawList);
  return list
    .map((item) => (typeof item === "string" ? item : (item?.name ?? item?.value ?? item?.id)))
    .filter((s) => s != null && String(s).trim() !== "");
}

/** Simple { value, label } options from string labels (detail view fallbacks). */
export function labelsToSimpleOptions(labels) {
  if (!Array.isArray(labels)) return [];
  return labels.map((label) => ({ value: label, label }));
}

/** Kanban inline create / compact dropdowns: { label, value } from system list rows. */
export function mapSystemListToKanbanInlineOptions(rawList) {
  const arr = Array.isArray(rawList) ? rawList : [];
  return arr.map((item) => ({
    label: item?.name ?? item?.value ?? String(item?.id ?? ""),
    value: item?.id ?? item?.value ?? item?.name ?? null,
  }));
}

/** Assignee display string: firstName + lastName or "N/A". Never email. */
export function getAssigneeDisplay(assignee) {
  if (assignee == null) return null;
  if (typeof assignee === "object") {
    const name = [assignee.firstName, assignee.lastName].filter(Boolean).join(" ").trim();
    return name || "N/A";
  }
  const s = String(assignee).trim();
  if (s.includes("@")) return null;
  return s || null;
}

/* -------------------------------------------------------------------------- */
/* Static options & display config (fallbacks when system lists are empty)    */
/* -------------------------------------------------------------------------- */

/** Tailwind-free class names; colors from `theme.css` (--work-item-type-*). */
export const TYPE_ICON_MAP = {
  /** Jira-style epic: lightning bolt (outline). */
  Epic: { icon: LuZap, color: "work-item-type-icon--epic" },
  Task: { icon: RiTaskLine, color: "work-item-type-icon--task" },
  Bug: { icon: RiBugLine, color: "work-item-type-icon--bug" },
  Story: { icon: RiTaskLine, color: "work-item-type-icon--task" },
};

export const TYPE_FALLBACK_OPTIONS = [
  { value: "Epic", label: "Epic", icon: LuZap, color: "work-item-type-icon--epic" },
  { value: "Task", label: "Task", icon: RiTaskLine, color: "work-item-type-icon--task" },
  { value: "Bug", label: "Bug", icon: RiBugLine, color: "work-item-type-icon--bug" },
];

/** Type ids (or fallback value strings) for Task/Bug — parent epic is required on create/update forms. */
export function getWorkItemTypeIdsRequiringParentEpic(typeList) {
  const ids = new Set();
  const list = Array.isArray(typeList) ? typeList : [];
  if (list.length === 0) {
    ids.add("Task");
    ids.add("Bug");
    return ids;
  }
  for (const item of list) {
    const n = String(item?.name ?? item?.value ?? "")
      .trim()
      .toLowerCase();
    if ((n === "task" || n === "bug") && item?.id != null) {
      ids.add(String(item.id).trim());
    }
  }
  return ids;
}

export const PRIORITY_ICON_MAP = {
  Highest: { icon: RiArrowUpDoubleLine, color: "text-red-500" },
  High: { icon: RiArrowUpSLine, color: "text-orange-500" },
  Medium: { icon: RiSubtractLine, color: "text-amber-600" },
  Lowest: { icon: RiArrowDownDoubleLine, color: "text-blue-500" },
  Low: { icon: RiArrowDownDoubleLine, color: "text-blue-500" },
  Urgent: { icon: RiArrowUpDoubleLine, color: "text-red-500" },
};

export const PRIORITY_FALLBACK_OPTIONS = [
  { value: "Highest", label: "Highest", icon: RiArrowUpDoubleLine, color: "text-red-500" },
  { value: "High", label: "High", icon: RiArrowUpSLine, color: "text-orange-500" },
  { value: "Medium", label: "Medium", icon: RiSubtractLine, color: "text-amber-600" },
  { value: "Lowest", label: "Lowest", icon: RiArrowDownDoubleLine, color: "text-blue-500" },
];

/**
 * Default create-form priority: Medium row’s **id** from the PRIORITY system list only.
 * Returns "" until the list loads so the form never stores the label `"Medium"` as `priorityId`.
 */
export function resolveDefaultMediumPriorityId(priorityList) {
  if (!Array.isArray(priorityList) || priorityList.length === 0) {
    return "";
  }
  const found = priorityList.find((p) => {
    const n = String(p?.name ?? p?.label ?? p?.displayName ?? "")
      .trim()
      .toLowerCase();
    return n === "medium";
  });
  if (!found) return "";
  const id = getSystemListItemId(found);
  return id != null && String(id).trim() !== "" ? String(id).trim() : "";
}

/**
 * Coerce form `priority` (dropdown value) to API `priorityId`: always prefer system-list id,
 * never send a bare label like `"Medium"` when the list provides a UUID.
 */
export function resolvePriorityIdForWorkItemApi(priorityRaw, priorityList, priorityOptions = []) {
  if (priorityRaw == null) return null;
  const raw = String(priorityRaw).trim();
  if (raw === "") return null;

  const opts = Array.isArray(priorityOptions) ? priorityOptions : [];
  const list = Array.isArray(priorityList) ? priorityList : [];

  const idFromRow = (row) => {
    if (row == null || typeof row !== "object") return null;
    const id = getSystemListItemId(row);
    return id != null && String(id).trim() !== "" ? String(id).trim() : null;
  };

  for (const row of list) {
    const id = idFromRow(row);
    if (id && id === raw) return id;
  }

  for (const o of opts) {
    const v = o?.value != null ? String(o.value).trim() : "";
    if (v && v === raw) return v;
  }

  const rawLower = raw.toLowerCase();
  for (const o of opts) {
    const lab = String(o?.label ?? "")
      .trim()
      .toLowerCase();
    if (lab && lab === rawLower) {
      const v = o?.value != null ? String(o.value).trim() : "";
      if (v) return v;
    }
  }

  for (const row of list) {
    const id = idFromRow(row);
    if (!id) continue;
    const name = String(row?.name ?? row?.label ?? row?.displayName ?? "")
      .trim()
      .toLowerCase();
    if (name === rawLower) return id;
  }

  return raw;
}

export const ENVIRONMENT_FALLBACK_OPTIONS = [
  { value: "Development", label: "Development" },
  { value: "Production", label: "Production" },
  { value: "Staging", label: "Staging" },
  { value: "Testing", label: "Testing" },
];

export const REASON_OF_BUG_OPTIONS = [
  { value: "Unclear requirements", label: "Unclear requirements" },
  { value: "Coding issues", label: "Coding issues" },
  { value: "Environment issues", label: "Environment issues" },
  { value: "Security issues", label: "Security issues" },
  { value: "Client side issue", label: "Client side issue" },
];

export const LABEL_FALLBACK_OPTIONS = [
  { value: "Ticketing", label: "Ticketing" },
  { value: "Not_Started", label: "Not_Started" },
  { value: "Not", label: "Not" },
  { value: "testing", label: "testing" },
  { value: "doing", label: "doing" },
  { value: "reports", label: "reports" },
];

export const DEFAULT_WORK_ITEM_TYPE_FILTER_OPTIONS = ["Task", "Bug", "Epic", "Story"];
export const DEFAULT_PRIORITY_FILTER_OPTIONS = ["Low", "Medium", "High", "Critical"];
export const DEFAULT_STATUS_FILTER_OPTIONS = ["Open", "In Progress", "Blocked", "Done"];

/** GET /work-items?statusCategories= — backend enum (underscore). */
export const WORK_ITEM_STATUS_CATEGORY_API = {
  NEW: "NEW",
  ESTIMATIONS: "ESTIMATIONS",
  IN_REVIEW: "IN_REVIEW",
  IN_PROGRESS: "IN_PROGRESS",
  /** Backend `statusCategories` value (not `TESTING`). */
  TESTING: "TO_TESTING",
  ON_HOLD: "ON_HOLD",
  /** Terminal column — backend uses `CLOSED` (legacy `DONE` still accepted client-side). */
  CLOSED: "CLOSED",
};

/** All Issues / My work items: Status filter = six default workflow categories. */
export const ALL_ISSUES_STATUS_CATEGORY_FILTER_OPTIONS =
  DEFAULT_WORK_ITEM_STATUS_CATEGORY_FILTER_OPTIONS;

const WORK_ITEM_STATUS_CATEGORY_API_VALUE_SET = new Set([
  ...DEFAULT_WORK_ITEM_STATUS_CATEGORY_ORDER,
  ...Object.values(WORK_ITEM_STATUS_CATEGORY_API),
  /** Legacy tokens (URLs / older API). */
  "TESTING",
  "TODO",
  "TO_DO",
  "OPEN",
  "CLOSED",
  "COMPLETED",
]);

export function isWorkItemStatusCategoryApiValue(value) {
  const v = String(value ?? "")
    .trim()
    .toUpperCase()
    .replace(/\s+/g, "_");
  if (WORK_ITEM_STATUS_CATEGORY_API_VALUE_SET.has(v)) return true;
  const normalized = normalizeWorkItemStatusCategoryKey(v);
  return Boolean(normalized && WORK_ITEM_STATUS_CATEGORY_THEME[normalized]);
}

function workItemRowIsNewCategory(issue) {
  const cat = String(issue?.statusCategory ?? "")
    .trim()
    .toUpperCase()
    .replace(/\s+/g, "_");
  const name = String(issue?.status ?? "")
    .trim()
    .toLowerCase();
  if (/^(TODO|TO_DO|OPEN|NEW)$/.test(cat)) return true;
  if (/to\s*do|^todo$|^open$|^new$/.test(name)) return true;
  return false;
}

function workItemRowIsDoneCategory(issue) {
  const cat = String(issue?.statusCategory ?? "")
    .trim()
    .toUpperCase()
    .replace(/\s+/g, "_");
  const name = String(issue?.status ?? "")
    .trim()
    .toLowerCase();
  if (/^(DONE|COMPLETED|CLOSED)$/.test(cat)) return true;
  if (/done|closed|complete|resolved/.test(name)) return true;
  return false;
}

/** Client-side match for `statusCategories` filter (when list is not server-filtered). */
export function issueMatchesWorkItemStatusCategory(issue, categoryApiValue) {
  let want = String(categoryApiValue ?? "")
    .trim()
    .toUpperCase()
    .replace(/\s+/g, "_");
  if (want === "DONE") want = "CLOSED";
  if (want === "TODO" || want === "TO_DO" || want === "OPEN") want = "NEW";
  if (!want) return true;

  const categoryFromNestedStatus =
    typeof issue?.status === "object" && issue?.status != null
      ? (issue.status.category ?? issue.status.statusCategory)
      : "";
  const cat = String(issue?.statusCategory ?? categoryFromNestedStatus ?? "")
    .trim()
    .toUpperCase()
    .replace(/\s+/g, "_");

  if (cat) {
    if (want === WORK_ITEM_STATUS_CATEGORY_API.IN_PROGRESS) {
      return cat === "IN_PROGRESS" || cat === "BLOCKED";
    }
    if (want === WORK_ITEM_STATUS_CATEGORY_API.TESTING) {
      return cat === "TO_TESTING" || cat === "TESTING";
    }
    if (want === WORK_ITEM_STATUS_CATEGORY_API.CLOSED) {
      return cat === "CLOSED" || cat === "DONE" || cat === "COMPLETED";
    }
    if (want === WORK_ITEM_STATUS_CATEGORY_API.NEW) {
      return cat === "NEW" || cat === "TODO" || cat === "TO_DO" || cat === "OPEN";
    }
    return cat === want;
  }

  if (
    want === WORK_ITEM_STATUS_CATEGORY_API.ESTIMATIONS ||
    want === WORK_ITEM_STATUS_CATEGORY_API.IN_REVIEW
  ) {
    return false;
  }

  const statusNameLower = String(issue?.status ?? "")
    .trim()
    .toLowerCase();

  if (want === WORK_ITEM_STATUS_CATEGORY_API.TESTING) {
    if (!statusNameLower) return false;
    if (/estimat/i.test(statusNameLower)) return false;
    return (
      /\btesting\b/.test(statusNameLower) ||
      /\bqa\b/.test(statusNameLower) ||
      /\buat\b/.test(statusNameLower) ||
      /in\s+qa\b/.test(statusNameLower) ||
      /quality\s+assurance/.test(statusNameLower)
    );
  }
  if (want === WORK_ITEM_STATUS_CATEGORY_API.ON_HOLD) {
    if (!statusNameLower) return false;
    return (
      /\bon\s*hold\b/.test(statusNameLower) ||
      /\bparked\b/.test(statusNameLower) ||
      /\bdeferred\b/.test(statusNameLower) ||
      /\bpending\s+external\b/.test(statusNameLower)
    );
  }

  const isNew = workItemRowIsNewCategory(issue);
  const done = workItemRowIsDoneCategory(issue);
  if (want === WORK_ITEM_STATUS_CATEGORY_API.NEW) return isNew;
  if (want === WORK_ITEM_STATUS_CATEGORY_API.CLOSED) return done;
  if (want === WORK_ITEM_STATUS_CATEGORY_API.IN_PROGRESS) return !isNew && !done;
  return false;
}

export const ISSUE_LIST_SAVED_FILTER_PRESETS = [
  { id: "assigned-to-me", label: "Assigned to me" },
  { id: "my-open-work-items", label: "My open work items" },
  { id: "reported-by-me", label: "Reported by me" },
  { id: "open-work-items", label: "Open work items" },
  { id: "done-work-items", label: "Done work items" },
  { id: "viewed-recently", label: "Viewed recently" },
  { id: "resolved-recently", label: "Resolved recently" },
  { id: "updated-recently", label: "Updated recently" },
];

export const PRIORITY_LABEL_FALLBACKS = ["Lowest", "Low", "Medium", "High", "Highest"];
export const SEVERITY_LABEL_FALLBACKS = ["Minor", "Major", "Critical", "Blocker"];
export const ENVIRONMENT_LABEL_FALLBACKS = ["Development", "Production", "Staging", "Testing"];

export const KANBAN_INLINE_ISSUE_TYPES = [
  { key: "Epic", label: "Epic", color: "work-item-type-icon--epic", icon: LuZap },
  { key: "Task", label: "Task", color: "work-item-type-icon--task", icon: RiTaskLine },
  { key: "Bug", label: "Bug", color: "work-item-type-icon--bug", icon: RiBugLine },
];

export const QUICK_CREATE_TYPE_OPTIONS = [
  { value: "Epic", label: "Epic", icon: LuZap },
  { value: "Task", label: "Task", icon: RiTaskLine },
  { value: "Bug", label: "Bug", icon: RiBugLine },
];

export const WORK_ITEM_TYPE_TEXT_CLASS = {
  Epic: "work-item-type-icon--epic",
  Task: "work-item-type-icon--task",
  Bug: "work-item-type-icon--bug",
};

/** UUID-shaped strings — avoid treating them as human-readable type names. */
export function looksLikeUuid(value) {
  const s = String(value ?? "").trim();
  return /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(s);
}

function workItemTypeListRowId(row) {
  if (row == null || typeof row !== "object") return "";
  const nested = row.tag && typeof row.tag === "object" ? row.tag : row;
  return String(
    nested.id ?? nested.listItemId ?? nested.systemListItemId ?? nested.value ?? row.value ?? ""
  ).trim();
}

function workItemTypeListRowName(row) {
  if (row == null || typeof row !== "object") return "";
  const nested = row.tag && typeof row.tag === "object" ? row.tag : row;
  return String(nested.name ?? nested.label ?? nested.title ?? row.name ?? row.label ?? "").trim();
}

/**
 * Human-readable type name for UI (breadcrumb, headers). Resolves `typeId` via system list when `type` is missing or a UUID.
 */
export function resolveWorkItemTypeDisplayName(issueLike, workItemTypesRaw = []) {
  const rawType = String(issueLike?.type ?? "").trim();
  if (rawType && !looksLikeUuid(rawType)) return rawType;
  const typeId =
    issueLike?.typeId != null && String(issueLike.typeId).trim() !== ""
      ? String(issueLike.typeId).trim()
      : "";
  if (typeId && Array.isArray(workItemTypesRaw)) {
    const row = workItemTypesRaw.find((t) => workItemTypeListRowId(t) === typeId);
    const name = row ? workItemTypeListRowName(row) : "";
    if (name) return name;
  }
  if (rawType && !looksLikeUuid(rawType)) return rawType;
  return rawType || "Work item";
}

/**
 * Distinct icons + theme color class for Epic / Task / Bug (Story uses task styling).
 */
export function getWorkItemTypeIconConfig(typeLabel) {
  const label = String(typeLabel ?? "")
    .trim()
    .toLowerCase();
  if (label === "epic" || label.includes("epic")) {
    return { Icon: LuZap, colorClass: "work-item-type-icon--epic" };
  }
  if (label === "bug" || label.includes("bug")) {
    return { Icon: RiBugLine, colorClass: "work-item-type-icon--bug" };
  }
  if (label === "task" || label === "story" || label.includes("task") || label.includes("story")) {
    return { Icon: RiTaskLine, colorClass: "work-item-type-icon--task" };
  }
  return { Icon: RiTaskLine, colorClass: "work-item-type-icon--default" };
}

/** Parent column / breadcrumb: title or summary; never raw parent UUID. */
export function getWorkItemParentDisplayName(issue) {
  const title = String(issue?.parentTitle ?? "").trim();
  if (title) return title;
  const p = issue?.parent;
  if (typeof p === "object" && p != null) {
    const fromParent = String(p.title ?? p.summary ?? "").trim();
    if (fromParent) return fromParent;
    const key = String(p.itemKey ?? p.issueKey ?? "").trim();
    if (key) return key;
  }
  const raw = issue?.parent;
  if (typeof raw === "string" && raw.trim() && !looksLikeUuid(raw)) return raw.trim();
  return "";
}

export const ISSUE_DETAILS_SUBTABS = [
  { id: "comments", label: "Comments" },
  { id: "history", label: "History" },
  { id: "worklog", label: "Worklog" },
];

export const ISSUE_LIST_DEFAULT_CELL_BADGE_STYLE = {
  backgroundColor: "#F3F4F6",
  color: "#6B7280",
  borderColor: "#D1D5DB",
};

export const ISSUE_LIST_PRIORITY_BORDER_COLORS = {
  low: "#B7E4C7",
  medium: "#FCD34D",
  high: "#FCA5A5",
  critical: "#F87171",
  highest: "#F43F5E",
  lowest: "#F87171",
  urgent: "#FCD34D",
};

export const ISSUE_LIST_PRIORITY_BADGE_STYLES = {
  low: {
    backgroundColor: "#E8F8F0",
    color: "#0F9D58",
    borderColor: "#B7E4C7",
  },
  medium: {
    backgroundColor: "#FFF4E5",
    color: "#F59E0B",
    borderColor: "#FCD34D",
  },
  high: {
    backgroundColor: "#FDECEC",
    color: "#DC2626",
    borderColor: "#FCA5A5",
  },
  critical: {
    backgroundColor: "#FCE8E8",
    color: "#991B1B",
    borderColor: "#F87171",
  },
  highest: {
    backgroundColor: "#FFE4E6",
    color: "#BE123C",
    borderColor: "#F43F5E",
  },
  lowest: {
    backgroundColor: "#FCE8E8",
    color: "#991B1B",
    borderColor: "#F87171",
  },
  urgent: {
    backgroundColor: "#FEF3C7",
    color: "#B45309",
    borderColor: "#FCD34D",
  },
};

/** List status badge for New / To Do / Open (indigo). */
const ISSUE_LIST_NEW_STATUS_BADGE_STYLE = {
  backgroundColor: "#EEF2FF",
  color: "#4338CA",
  borderColor: "#C7D2FE",
};

export const ISSUE_LIST_STATUS_BADGE_STYLES = {
  open: ISSUE_LIST_NEW_STATUS_BADGE_STYLE,
  "to do": ISSUE_LIST_NEW_STATUS_BADGE_STYLE,
  new: ISSUE_LIST_NEW_STATUS_BADGE_STYLE,
  "in progress": {
    backgroundColor: "#F0F9FF",
    color: "#0284C7",
    borderColor: "#BAE6FD",
  },
  blocked: {
    backgroundColor: "#FEF2F2",
    color: "#B91C1C",
    borderColor: "#FECACA",
  },
  done: {
    backgroundColor: "#ECFDF5",
    color: "#047857",
    borderColor: "#A7F3D0",
  },
  closed: {
    backgroundColor: "#ECFDF5",
    color: "#047857",
    borderColor: "#A7F3D0",
  },
};

/** Status-category badge colors (six defaults + legacy API aliases). */
export const ISSUE_LIST_STATUS_CATEGORY_BADGE_STYLES = {
  ...Object.fromEntries(
    Object.entries(WORK_ITEM_STATUS_CATEGORY_THEME).map(([key, entry]) => [key, entry.badge])
  ),
  TODO: WORK_ITEM_STATUS_CATEGORY_THEME.NEW.badge,
  OPEN: WORK_ITEM_STATUS_CATEGORY_THEME.NEW.badge,
  DONE: WORK_ITEM_STATUS_CATEGORY_THEME.CLOSED.badge,
  COMPLETED: WORK_ITEM_STATUS_CATEGORY_THEME.CLOSED.badge,
  TO_TESTING: WORK_ITEM_STATUS_CATEGORY_THEME.IN_REVIEW.badge,
  TESTING: WORK_ITEM_STATUS_CATEGORY_THEME.IN_REVIEW.badge,
  BLOCKED: {
    backgroundColor: "#FEF2F2",
    color: "#B91C1C",
    borderColor: "#FECACA",
  },
};

export const KANBAN_THEME_BY_GROUP = {
  ...Object.fromEntries(
    Object.entries(WORK_ITEM_STATUS_CATEGORY_THEME).map(([key, entry]) => [key, entry.kanbanClass])
  ),
  TODO: WORK_ITEM_STATUS_CATEGORY_THEME.NEW.kanbanClass,
  OPEN: WORK_ITEM_STATUS_CATEGORY_THEME.NEW.kanbanClass,
  DONE: WORK_ITEM_STATUS_CATEGORY_THEME.CLOSED.kanbanClass,
  COMPLETED: WORK_ITEM_STATUS_CATEGORY_THEME.CLOSED.kanbanClass,
  TO_TESTING: WORK_ITEM_STATUS_CATEGORY_THEME.IN_REVIEW.kanbanClass,
  TESTING: WORK_ITEM_STATUS_CATEGORY_THEME.IN_REVIEW.kanbanClass,
  BLOCKED: "border-red-200 bg-red-50 text-red-800",
};

export function resolveKanbanThemeGroup(status) {
  const rawCategory = String(status?.category ?? "").trim();
  const key = rawCategory.replace(/\s+/g, "_").toUpperCase();
  if (/^(TODO|TO_DO|OPEN|NEW)$/.test(key)) return "NEW";
  if (/^(ESTIMATIONS?)$/.test(key)) return "ESTIMATIONS";
  if (/^(IN_REVIEW|REVIEW)$/.test(key)) return "IN_REVIEW";
  if (/^(IN_PROGRESS|INPROGRESS|IN-PROGRESS)$/.test(key)) return "IN_PROGRESS";
  if (/^(TESTING|TO_TESTING|IN_TESTING|QA)$/.test(key)) return "TESTING";
  if (/^(ON_HOLD|ONHOLD|HOLD)$/.test(key)) return "ON_HOLD";
  if (/^(BLOCKED)$/.test(key)) return "BLOCKED";
  if (/^(CLOSED|DONE|COMPLETED)$/.test(key)) return "CLOSED";

  const name = String(status?.name ?? "")
    .trim()
    .toLowerCase();
  if (/blocked/.test(name)) return "BLOCKED";
  if (/estimat/.test(name)) return "ESTIMATIONS";
  if (/review/.test(name)) return "IN_REVIEW";
  if (/on\s*hold|parked|deferred/.test(name)) return "ON_HOLD";
  if (/\btesting\b|\bqa\b|\buat\b|quality\s+assurance/.test(name) && !/estimat/.test(name))
    return "TESTING";
  if (/in\s*progress|doing/.test(name)) return "IN_PROGRESS";
  if (/to\s*do|todo|open|new/.test(name)) return "NEW";
  if (/done|closed|complete/.test(name)) return "CLOSED";
  return "IN_PROGRESS";
}

export function resolveKanbanColumnTheme(status) {
  const group = resolveKanbanThemeGroup(status);
  return KANBAN_THEME_BY_GROUP[group] ?? KANBAN_THEME_BY_GROUP.CLOSED;
}

/** @deprecated Scrum / legacy — one column per workflow status. Prefer {@link buildProjectKanbanCategoryColumns} for project Kanban. */
export function buildKanbanColumnsFromStatuses(statusesResponse) {
  const raw = statusesResponse?.data;
  const list = Array.isArray(raw) ? raw : [];
  const sorted = [...list].sort((a, b) => (a.sequenceOrder ?? 0) - (b.sequenceOrder ?? 0));
  const createSource =
    sorted.find((s) => /^(todo|to_do|new|open)$/i.test(String(s.category ?? "").trim())) ??
    sorted[0];
  return sorted.map((s) => ({
    key: String(s.id),
    statusId: s.id,
    label: s.name?.trim() ? String(s.name) : "Status",
    category: s.category,
    color: resolveKanbanColumnTheme(s),
    isCreateColumn: createSource != null && s.id === createSource.id,
  }));
}

export function projectKanbanColumnKeyFromCategory(statusCategory) {
  const normalized = normalizeWorkItemStatusCategoriesForApi(statusCategory);
  const token = Array.isArray(normalized) ? normalized[0] : normalized;
  if (!token) return "project-unknown";
  return `project-${String(token).toLowerCase().replace(/_/g, "-")}`;
}

/** Default workflow status id when dropping or creating in a category column. */
export function resolveProjectStatusIdForCategory(statusesResponse, category) {
  const list = Array.isArray(statusesResponse?.data)
    ? statusesResponse.data
    : Array.isArray(statusesResponse)
      ? statusesResponse
      : [];
  const want = workItemStatusCategorySortKey(category);
  const matches = list.filter((s) => workItemStatusCategorySortKey(s?.category) === want);
  matches.sort((a, b) => (a.sequenceOrder ?? 0) - (b.sequenceOrder ?? 0));
  const id = matches[0]?.id;
  return id != null && String(id).trim() !== "" ? id : null;
}

/** Locate a work item in paginated Kanban column state (global or project board). */
export function findIssueInKanbanColumnData(columnData, issueId) {
  if (!columnData || issueId == null) return null;
  for (const col of Object.values(columnData)) {
    const items = col?.items;
    if (!Array.isArray(items)) continue;
    const hit = items.find((i) => String(i?.id) === String(issueId));
    if (hit) return hit;
  }
  return null;
}

/** Five category columns for project Kanban (New → Estimations → In Progress → In Review → Done). */
export function buildProjectKanbanCategoryColumns(statusesResponse) {
  return PROJECT_KANBAN_STATUS_CATEGORY_ORDER.map((category, index) => {
    const statusId = resolveProjectStatusIdForCategory(statusesResponse, category);
    return {
      key: projectKanbanColumnKeyFromCategory(category),
      statusId,
      label: getWorkItemStatusCategoryLabel(category),
      category,
      color: getWorkItemStatusCategoryKanbanClass(category),
      isCreateColumn: index === 0 && statusId != null,
    };
  });
}

export function resolveProjectKanbanColumnKey(issue, statusesResponse) {
  const cat = String(issue?.statusCategory ?? "")
    .trim()
    .toUpperCase()
    .replace(/\s+/g, "_");
  if (cat) {
    let mapped = cat;
    if (cat === "DONE" || cat === "COMPLETED" || cat === "CLOSED") mapped = "DONE";
    else if (cat === "TODO" || cat === "TO_DO" || cat === "OPEN") mapped = "NEW";
    return projectKanbanColumnKeyFromCategory(mapped);
  }

  const statusId = issue?.statusId;
  if (statusId != null && statusesResponse?.data) {
    const row = statusesResponse.data.find((s) => String(s.id) === String(statusId));
    if (row?.category) return projectKanbanColumnKeyFromCategory(row.category);
  }

  const group = resolveKanbanThemeGroup({
    category: issue?.statusCategory,
    name: typeof issue?.status === "string" ? issue.status : issue?.status?.name,
  });
  const themeKey = group === "CLOSED" ? "DONE" : group;
  return projectKanbanColumnKeyFromCategory(themeKey);
}

/** Fixed columns for /issues Kanban (no project workflow). Not draggable. */
export const GLOBAL_KANBAN_COLUMN_KEYS = {
  NEW: "global-new",
  IN_PROGRESS: "global-in-progress",
  CLOSED: "global-closed",
};

/** Stable column key from API `statusCategory` (e.g. NEW → global-new, IN_REVIEW → global-in-review). */
export function globalKanbanColumnKeyFromCategory(statusCategory) {
  const cat = String(statusCategory ?? "")
    .trim()
    .toUpperCase()
    .replace(/\s+/g, "_");
  if (!cat) return "global-unknown";
  return `global-${cat.toLowerCase().replace(/_/g, "-")}`;
}

/** Human label for a global Kanban `statusCategory` code from the API. */
export function formatStatusCategoryLabel(statusCategory) {
  const key = normalizeWorkItemStatusCategoryKey(statusCategory);
  if (!key) return "Status";
  if (WORK_ITEM_STATUS_CATEGORY_THEME[key]) {
    return getWorkItemStatusCategoryLabel(key);
  }
  const cat = String(statusCategory ?? "")
    .trim()
    .toUpperCase()
    .replace(/\s+/g, "_");
  return cat
    .toLowerCase()
    .split("_")
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(" ");
}

export function resolveGlobalKanbanColumnTheme(statusCategory) {
  const group = resolveKanbanThemeGroup({ category: statusCategory });
  return KANBAN_THEME_BY_GROUP[group] ?? KANBAN_THEME_BY_GROUP.IN_PROGRESS;
}

/** Display order for global Kanban columns (defaults first, then legacy API tokens). */
export const GLOBAL_KANBAN_STATUS_CATEGORY_ORDER = [
  ...KANBAN_BOARD_STATUS_CATEGORY_ORDER,
  "TODO",
  "OPEN",
  "TO_TESTING",
  "TESTING",
  "BLOCKED",
  "DONE",
];

function globalKanbanCategorySortIndex(category) {
  return getKanbanStatusCategorySortIndex(category);
}

/** Sort global Kanban column defs into workflow order regardless of API column order. */
export function sortGlobalKanbanColumns(columns) {
  if (!Array.isArray(columns) || columns.length <= 1) return columns ?? [];
  return [...columns].sort((a, b) => {
    const orderDiff =
      globalKanbanCategorySortIndex(a.category) - globalKanbanCategorySortIndex(b.category);
    if (orderDiff !== 0) return orderDiff;
    return String(a.label ?? "").localeCompare(String(b.label ?? ""));
  });
}

/**
 * Build global Kanban column defs from GET /work-items/kanban/initial response.
 * Uses `data.columns[].statusCategory` order, else `data.statusCategories`.
 */
export function buildGlobalKanbanColumnsFromBootstrap(raw) {
  const data = raw?.data ?? raw;
  const apiColumns = Array.isArray(data?.columns) ? data.columns : [];
  const categoriesFromColumns = apiColumns
    .map((c) => c?.statusCategory)
    .filter((c) => c != null && String(c).trim() !== "");
  const categories =
    categoriesFromColumns.length > 0
      ? categoriesFromColumns
      : Array.isArray(data?.statusCategories)
        ? data.statusCategories
        : [];

  if (!categories.length) return buildGlobalKanbanColumns();

  const built = categories.map((cat) => {
    const category = String(cat).trim().toUpperCase().replace(/\s+/g, "_");
    return {
      key: globalKanbanColumnKeyFromCategory(category),
      statusId: null,
      label: formatStatusCategoryLabel(category),
      category,
      color: resolveGlobalKanbanColumnTheme(category),
      isCreateColumn: false,
    };
  });

  return sortGlobalKanbanColumns(built).map((col, index) => ({
    ...col,
    isCreateColumn: index === 0,
  }));
}

export function buildGlobalKanbanColumns({ includeOnHold = false } = {}) {
  const categories = includeOnHold
    ? KANBAN_BOARD_STATUS_CATEGORY_ORDER
    : KANBAN_BOARD_STATUS_CATEGORY_ORDER.filter((c) => c !== "ON_HOLD");
  return categories.map((category, index) => ({
    key: globalKanbanColumnKeyFromCategory(category),
    statusId: null,
    label: getWorkItemStatusCategoryLabel(category),
    category,
    color: getWorkItemStatusCategoryKanbanClass(category),
    isCreateColumn: index === 0,
  }));
}

/** Single On Hold column for global Kanban when `showOnHold` is enabled. */
export function createGlobalKanbanOnHoldColumnDef() {
  return {
    key: globalKanbanColumnKeyFromCategory("ON_HOLD"),
    statusId: null,
    label: getWorkItemStatusCategoryLabel("ON_HOLD"),
    category: "ON_HOLD",
    color: getWorkItemStatusCategoryKanbanClass("ON_HOLD"),
    isCreateColumn: false,
  };
}

/**
 * When `showOnHold` is true, ensure an On Hold column exists and place it last.
 * When false, remove any On Hold column from the board.
 */
export function applyGlobalKanbanOnHoldColumn(columns, showOnHold) {
  const list = Array.isArray(columns) ? [...columns] : [];
  const isOnHold = (col) => normalizeWorkItemStatusCategoryKey(col?.category) === "ON_HOLD";
  if (!showOnHold) {
    return list.filter((col) => !isOnHold(col));
  }
  const without = list.filter((col) => !isOnHold(col));
  const onHoldCol = list.find(isOnHold) ?? createGlobalKanbanOnHoldColumnDef();
  return [...without, onHoldCol];
}

/**
 * Maps a work item to a global Kanban column key.
 * — New: new / todo / open-like statuses
 * — Done: done / closed / complete-like
 * — In Progress: everything else (in progress, blocked, unknown)
 */
export function resolveGlobalKanbanColumnKey(issue) {
  const cat = String(issue?.statusCategory ?? "")
    .trim()
    .toUpperCase()
    .replace(/\s+/g, "_");
  if (cat) {
    let mapped = cat;
    if (cat === "DONE" || cat === "COMPLETED" || cat === "CLOSED") mapped = "DONE";
    else if (cat === "TODO" || cat === "TO_DO" || cat === "OPEN") mapped = "NEW";
    return globalKanbanColumnKeyFromCategory(mapped);
  }

  const name = String(issue?.status ?? "")
    .trim()
    .toLowerCase();
  if (/blocked/.test(name)) return GLOBAL_KANBAN_COLUMN_KEYS.IN_PROGRESS;
  if (/in\s*progress|review|doing/.test(name)) return GLOBAL_KANBAN_COLUMN_KEYS.IN_PROGRESS;
  if (/to\s*do|^todo$|^open$|^new$/.test(name)) return GLOBAL_KANBAN_COLUMN_KEYS.NEW;
  if (/done|closed|complete|resolved/.test(name)) {
    return globalKanbanColumnKeyFromCategory("DONE");
  }
  return GLOBAL_KANBAN_COLUMN_KEYS.IN_PROGRESS;
}

/* -------------------------------------------------------------------------- */
/* Issue list row visuals (icons)                                              */
/* -------------------------------------------------------------------------- */

export function getIssueListPriorityBorderStyle(priority) {
  const key = String(priority ?? "")
    .trim()
    .toLowerCase();
  const borderColor = ISSUE_LIST_PRIORITY_BORDER_COLORS[key] ?? "#D1D5DB";
  return { borderLeftColor: borderColor };
}

export function getIssueListPriorityBadgeStyle(priority) {
  const key = String(priority ?? "")
    .trim()
    .toLowerCase();
  return ISSUE_LIST_PRIORITY_BADGE_STYLES[key] ?? ISSUE_LIST_DEFAULT_CELL_BADGE_STYLE;
}

function isIssueLikeForStatusBadge(v) {
  return (
    v != null &&
    typeof v === "object" &&
    !Array.isArray(v) &&
    ("status" in v || "statusCategory" in v)
  );
}

/**
 * Map work item row to a coarse status-category key for list badge colors.
 * Uses `statusCategory` when present, else display `status` name heuristics.
 */
export function resolveIssueListStatusBadgeCategoryKey(issueLike) {
  const cat = String(issueLike?.statusCategory ?? "")
    .trim()
    .toUpperCase()
    .replace(/\s+/g, "_");
  if (cat === "CLOSED" || cat === "DONE" || cat === "COMPLETED") return "CLOSED";
  if (cat === "NEW" || cat === "TODO" || cat === "TO_DO" || cat === "OPEN") return "NEW";
  if (cat === "ESTIMATIONS" || cat === "ESTIMATION") return "ESTIMATIONS";
  if (cat === "IN_PROGRESS" || cat === "INPROGRESS") return "IN_PROGRESS";
  if (cat === "IN_REVIEW" || cat === "REVIEW") return "IN_REVIEW";
  if (cat === "ON_HOLD" || cat === "ONHOLD" || cat === "HOLD") return "ON_HOLD";
  if (cat === "TO_TESTING" || cat === "TESTING" || cat === "IN_TESTING" || cat === "QA")
    return "TO_TESTING";

  const name = String(issueLike?.status ?? "")
    .trim()
    .toLowerCase();
  if (!name) return null;
  if (/estimat/i.test(name) && !/testing/i.test(name)) return "ESTIMATIONS";
  if (/in\s*review|code\s*review/i.test(name)) return "IN_REVIEW";
  if (/\bon\s*hold\b|parked|deferred/i.test(name)) return "ON_HOLD";
  if (/done|closed|complete|resolved/i.test(name)) return "CLOSED";
  if (
    (/\btesting\b|\bqa\b|\buat\b|quality\s+assurance/i.test(name) || /to\s*testing/i.test(name)) &&
    !/estimat/i.test(name)
  ) {
    return "TO_TESTING";
  }
  if (/to\s*do|^todo$|^open$|^new$/.test(name)) return "NEW";
  if (/in\s*progress|doing/.test(name)) return "IN_PROGRESS";
  return null;
}

export function getIssueListStatusBadgeStyle(statusOrIssue) {
  if (isIssueLikeForStatusBadge(statusOrIssue)) {
    const catKey = resolveIssueListStatusBadgeCategoryKey(statusOrIssue);
    if (catKey) {
      const normalized = normalizeWorkItemStatusCategoryKey(catKey);
      if (WORK_ITEM_STATUS_CATEGORY_THEME[normalized]) {
        return getWorkItemStatusCategoryBadgeStyle(normalized);
      }
      if (ISSUE_LIST_STATUS_CATEGORY_BADGE_STYLES[catKey]) {
        return ISSUE_LIST_STATUS_CATEGORY_BADGE_STYLES[catKey];
      }
    }
    const key = String(statusOrIssue.status ?? "")
      .trim()
      .toLowerCase();
    return ISSUE_LIST_STATUS_BADGE_STYLES[key] ?? ISSUE_LIST_DEFAULT_CELL_BADGE_STYLE;
  }
  const key = String(statusOrIssue ?? "")
    .trim()
    .toLowerCase();
  return ISSUE_LIST_STATUS_BADGE_STYLES[key] ?? ISSUE_LIST_DEFAULT_CELL_BADGE_STYLE;
}

const PRIORITY_ICON_BY_KEY = {
  low: RiArrowDownFill,
  medium: RiSubtractFill,
  high: RiArrowUpFill,
  critical: RiErrorWarningFill,
  highest: RiArrowUpCircleFill,
  lowest: RiArrowDownCircleFill,
  urgent: RiAlarmWarningFill,
};

export function getIssueListPriorityIcon(priority) {
  const key = String(priority ?? "")
    .trim()
    .toLowerCase();
  const Icon = PRIORITY_ICON_BY_KEY[key];
  if (!Icon) return null;
  return <Icon className="text-xs" />;
}

const STATUS_ICON_BY_KEY = {
  open: RiCircleFill,
  "to do": RiCircleFill,
  "in progress": RiPlayCircleFill,
  blocked: RiStopCircleFill,
  done: RiCheckboxCircleFill,
  closed: RiCheckboxCircleFill,
};

const STATUS_ICON_BY_CATEGORY = {
  NEW: RiCircleFill,
  ESTIMATIONS: RiFileList3Line,
  IN_PROGRESS: RiPlayCircleFill,
  IN_REVIEW: RiEyeLine,
  ON_HOLD: RiPauseCircleLine,
  TO_TESTING: RiTestTubeLine,
  CLOSED: RiCheckboxCircleFill,
  DONE: RiCheckboxCircleFill,
};

export function getIssueListStatusIcon(statusOrIssue) {
  if (isIssueLikeForStatusBadge(statusOrIssue)) {
    const catKey = resolveIssueListStatusBadgeCategoryKey(statusOrIssue);
    if (catKey && STATUS_ICON_BY_CATEGORY[catKey]) {
      const Icon = STATUS_ICON_BY_CATEGORY[catKey];
      return <Icon className="text-xs" />;
    }
    const key = String(statusOrIssue.status ?? "")
      .trim()
      .toLowerCase();
    const Icon = STATUS_ICON_BY_KEY[key];
    if (!Icon) return null;
    return <Icon className="text-xs" />;
  }
  const key = String(statusOrIssue ?? "")
    .trim()
    .toLowerCase();
  const Icon = STATUS_ICON_BY_KEY[key];
  if (!Icon) return null;
  return <Icon className="text-xs" />;
}
